import type {ValueOf} from 'type-fest';
import type {SocialTab} from '@libs/Navigation/types';
import type CONST from './CONST';
import type {DrinkingSessionId} from './types/onyx';
import type {UserID, DateString} from './types/onyx/OnyxCommon';

/**
 * Builds a URL with an encoded URI component for the `backTo` param which can be added to the end of URLs
 */
function getUrlWithBackToParam<TUrl extends string>(
  url: TUrl,
  backTo?: string,
): `${TUrl}` | `${TUrl}?backTo=${string}` | `${TUrl}&backTo=${string}` {
  const backToParam = backTo
    ? (`${url.includes('?') ? '&' : '?'}backTo=${encodeURIComponent(backTo)}` as const)
    : '';
  return `${url}${backToParam}` as const;
}

const PUBLIC_SCREENS_ROUTES = {
  ROOT: '',
  FORCE_UPDATE: 'force-update',
  INITIAL: 'initial',
  AUTH: 'auth',
  FORGOT_PASSWORD: 'forgot-password',
  DESKTOP_SIGN_IN_REDIRECT: 'desktop-signin-redirect',
  APPLE_SIGN_IN: 'sign-in-with-apple',
  GOOGLE_SIGN_IN: 'sign-in-with-google',
  TRANSITION_BETWEEN_APPS: 'transition',
} as const;

const ROUTES = {
  ...PUBLIC_SCREENS_ROUTES,

  HOME: 'home',

  BADGES: 'badges',

  DAY_OVERVIEW: {
    route: 'day-overview/:userID/:date',
    getRoute: (userID: UserID, date: DateString) =>
      `day-overview/${userID}/${date}` as const,
  },

  DRINKING_SESSION: {
    route: 'drinking-session/:sessionId',
    getRoute: (sessionId: DrinkingSessionId) =>
      `drinking-session/${sessionId}` as const,
  },
  DRINKING_SESSION_LIVE: {
    route: 'drinking-session/:sessionId/live',
    getRoute: (sessionId: DrinkingSessionId, backTo = '') =>
      getUrlWithBackToParam(
        `drinking-session/${sessionId}/live` as const,
        backTo,
      ),
  },
  DRINKING_SESSION_EDIT: {
    route: 'drinking-session/:sessionId/edit',
    getRoute: (sessionId: DrinkingSessionId, backTo = '') =>
      getUrlWithBackToParam(
        `drinking-session/${sessionId}/edit` as const,
        backTo,
      ),
  },
  DRINKING_SESSION_SESSION_DATE_SCREEN: {
    route: 'drinking-session/:sessionId/session-date-screen',
    getRoute: (sessionId: DrinkingSessionId, backTo = '') =>
      getUrlWithBackToParam(
        `drinking-session/${sessionId}/session-date-screen` as const,
        backTo,
      ),
  },
  DRINKING_SESSION_SESSION_NAME_SCREEN: {
    route: 'drinking-session/:sessionId/session-name-screen',
    getRoute: (sessionId: DrinkingSessionId, backTo = '') =>
      getUrlWithBackToParam(
        `drinking-session/${sessionId}/session-name-screen` as const,
        backTo,
      ),
  },
  DRINKING_SESSION_SESSION_NOTE_SCREEN: {
    route: 'drinking-session/:sessionId/session-note-screen',
    getRoute: (sessionId: DrinkingSessionId) =>
      `drinking-session/${sessionId}/session-note-screen` as const,
  },
  DRINKING_SESSION_SESSION_TIMEZONE_SCREEN: {
    route: 'drinking-session/:sessionId/session-timezone-screen',
    getRoute: (sessionId: DrinkingSessionId) =>
      `drinking-session/${sessionId}/session-timezone-screen` as const,
  },
  DRINKING_SESSION_SUMMARY: {
    route: 'drinking-session/:sessionId/summary',
    getRoute: (sessionId: DrinkingSessionId) =>
      `drinking-session/${sessionId}/summary` as const,
  },

  ONBOARDING_TERMS: 'onboarding/terms',
  ONBOARDING_DISPLAY_NAME: 'onboarding/display-name',

  SETTINGS: 'settings',

  SETTINGS_ABOUT: 'settings/about',
  SETTINGS_ACCOUNT: 'settings/account',
  SETTINGS_USER_NAME: 'settings/user-name',
  SETTINGS_DISPLAY_NAME: 'settings/display-name',
  SETTINGS_EMAIL: 'settings/email',
  SETTINGS_PASSWORD: 'settings/password',
  SETTINGS_CONNECTED_ACCOUNTS: 'settings/connected-accounts',
  SETTINGS_TIMEZONE: 'settings/timezone',
  SETTINGS_TIMEZONE_SELECT: 'settings/timezone-select',
  // SETTINGS_TIMEZONE_SELECT: {
  //   route: 'settings/timezone-select',
  //   getRoute: (selected: SelectedTimezone, backTo?: string) =>
  //     getUrlWithBackToParam(
  //       `settings/timezone-select?timezone=${selected}`,
  //       backTo,
  //     ),
  // },

  SETTINGS_APP_SHARE: 'settings/app-share',
  SETTINGS_PREFERENCES: 'settings/preferences',
  SETTINGS_PRIVACY: 'settings/privacy',
  SETTINGS_NOTIFICATIONS: 'settings/notifications',
  SETTINGS_PRIVACY_BLOCKED_USERS: 'settings/privacy/blocked-users',
  SETTINGS_LANGUAGE: 'settings/preferences/language',
  SETTINGS_THEME: 'settings/preferences/theme',
  SETTINGS_FIRST_DAY_OF_WEEK: 'settings/preferences/first-day-of-week',
  SETTINGS_UNITS_TO_COLORS: 'settings/preferences/units-to-colors',
  SETTINGS_DRINKS_TO_UNITS: 'settings/preferences/drinks-to-units',
  SETTINGS_COLOR_PALETTE: 'settings/preferences/color-palette',
  SETTINGS_COLOR_PALETTE_CUSTOM: 'settings/preferences/color-palette/custom',

  SETTINGS_SUPPORT: 'settings/support',
  SETTINGS_HELP: 'settings/help',
  SETTINGS_MANAGE_SUBSCRIPTION: 'settings/manage-subscription',
  SETTINGS_TERMS_OF_SERVICE: 'settings/terms-of-service',
  SETTINGS_PRIVACY_POLICY: 'settings/privacy-policy',
  SETTINGS_SUBSCRIPTION_TERMS: 'settings/subscription-terms',
  SETTINGS_REPORT_BUG: 'settings/report-bug',
  SETTINGS_FEEDBACK: 'settings/feedback',
  SETTINGS_MANAGE_ACCOUNT: 'settings/manage-account',
  SETTINGS_DELETE: 'settings/delete',
  SETTINGS_ADMIN: 'settings/admin',
  SETTINGS_ADMIN_FEEDBACK: 'settings/admin/feedback',
  SETTINGS_ADMIN_BUGS: 'settings/admin/bugs',

  PROFILE: {
    route: 'profile/:userID',
    getRoute: (userID: UserID) => `profile/${userID}` as const,
  },
  PROFILE_FRIENDS_FRIENDS: {
    route: 'profile/:userID/friends',
    getRoute: (userID: UserID) => `profile/${userID}/friends` as const,
  },
  PROFILE_REPORT_USER: {
    route: 'profile/:userID/report',
    // `objectPath` names one of the user's session photos when the report is
    // about that photo rather than the user in general (RFC §9).
    getRoute: (userID: UserID, objectPath?: string) =>
      objectPath
        ? (`profile/${userID}/report?objectPath=${encodeURIComponent(objectPath)}` as const)
        : (`profile/${userID}/report` as const),
  },

  SESSIONS_CALENDAR_FULLSCREEN: {
    route: 'sessions-calendar/:userID',
    getRoute: (userID: UserID, monthYear?: string) => {
      const qs = monthYear ? `?monthYear=${monthYear}` : '';
      return `sessions-calendar/${userID}${qs}` as const;
    },
  },

  SOCIAL: 'social',
  // The Friends tab with one of its top tabs pre-selected (read by SocialScreen
  // from the `tab` param). Link-building only; the linking config maps `social`.
  SOCIAL_TAB: {
    route: 'social',
    getRoute: (tab: SocialTab) => `social?tab=${tab}` as const,
  },

  // Causes an error
  // SOCIAL: {
  //   route: 'social/:screen',
  //   getRoute: (screen: DeepValueOf<typeof SCREENS.SOCIAL>) =>
  //     `social/${screen}` as const,
  // },
  SOCIAL_FRIEND_LIST: 'social/friend-list',
  SOCIAL_FRIEND_REQUESTS: 'social/friend-requests',
  // Add friends hub: the "Your code" tab (default) and the "Search" tab.
  SOCIAL_ADD_FRIENDS: {
    route: 'social/add-friends',
    getRoute: (tab?: ValueOf<typeof CONST.ADD_FRIENDS_TAB>) =>
      tab
        ? (`social/add-friends?tab=${tab}` as const)
        : ('social/add-friends' as const),
  },

  // Personal friend invite link target (https://app.kiroku.cz/add/<code>).
  // Registered in both the public and the signed-in root stacks.
  ADD_FRIEND: {
    route: 'add/:code',
    getRoute: (code: string) => `add/${code}` as const,
  },

  STATISTICS: 'statistics',
} as const;

export {getUrlWithBackToParam, PUBLIC_SCREENS_ROUTES};
export default ROUTES;

type ExtractRouteName<TRoute> = TRoute extends {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getRoute: (...args: any[]) => infer TRouteName;
}
  ? TRouteName
  : TRoute;

/**
 * Represents all routes in the app as a union of literal strings.
 */
type Route = {
  [K in keyof typeof ROUTES]: ExtractRouteName<(typeof ROUTES)[K]>;
}[keyof typeof ROUTES];

export type {Route};
